<script setup>
import { useElementBounding } from '@vueuse/core'
import { computed, inject, nextTick, onBeforeUnmount, onMounted, useTemplateRef, watch } from 'vue'
import { useStore } from 'vuex'

import { PROGRESS_MODULE_CELL_PLACEMENTS } from '@/utils/custom-dashboard-helper'
import { listStateInjectionKey } from '@/utils/injection-keys'
import { flattenOkrElements } from '@/utils/objectives'
import { useProgressViewPeriods } from '@/utils/progress-view'
import { disconnectSyncScroll, initSyncScroll, SYNC_AXES } from '@/utils/sync-scroll'

import ProgressModuleGridHeader from '@/components/dashboard/custom-dashboard/modules/progress-module/ProgressModuleGridHeader'
import ProgressModuleGridRow from '@/components/dashboard/custom-dashboard/modules/progress-module/ProgressModuleGridRow'
import SkeletonItem from '@/components/ui/SkeletonLoaders/SkeletonItem'
import VirtualizationList from '@/components/VirtualizationList'

defineOptions({
  name: 'ProgressView'
})

const props = defineProps({
  showFilterButtons: {
    type: Boolean
  },

  isNoElements: {
    type: Boolean
  }
})

const listState = inject(listStateInjectionKey)

const items = computed(() => {
  const rootElements = [...listState.rootElements].map(id => ({
    ...listState.okrElements[id]
  }))

  return flattenOkrElements(rootElements, 0, {
    listState,
    tableState: {
      showFilterButtons: props.showFilterButtons
    }
  })
})

const SYNC_SCROLL_GROUP_NAME = 'progress-view'

const setupSyncScroll = async () => {
  await nextTick()

  const elements = [
    {
      element: headerRef.value
    },
    {
      element: bodyRef.value
    }
  ]

  initSyncScroll({
    elements,
    syncAxes: [SYNC_AXES.X],
    groupName: SYNC_SCROLL_GROUP_NAME
  })
}

const headerRef = useTemplateRef('pv-header')
const bodyRef = useTemplateRef('pv-body')

onMounted(() => {
  setupSyncScroll()
})

onBeforeUnmount(() => {
  disconnectSyncScroll(SYNC_SCROLL_GROUP_NAME)
})

const { periodsForDisplay, minWidth } = useProgressViewPeriods({
  periods: () => listState.progressViewState.periods,
  granulationId: () => listState.progressViewState.granulationId
})

const { top } = useElementBounding(headerRef)
const store = useStore()

watch(
  () => top.value,
  newValue => {
    if (!newValue && props.isNoElements) return

    const stuckOffset = parseInt(getComputedStyle(headerRef.value).top)

    if (newValue <= stuckOffset) {
      store.dispatch('system/toggleSplitViewHeaderStickyStatus', true)
    } else {
      store.dispatch('system/toggleSplitViewHeaderStickyStatus', false)
    }
  }
)

onBeforeUnmount(() => {
  store.dispatch('system/toggleSplitViewHeaderStickyStatus', false)
})
</script>

<template>
  <div class="pv-Wrapper">
    <SkeletonItem
      v-if="listState.isInfiniteLoading"
      animation-opacity="0.8"
      class="pv-Wrapper_Loader"
      color="transparent"
      height="100%"
      width="100%"
    />
    <div ref="pv-header" class="pv-Header">
      <ProgressModuleGridHeader :periods="periodsForDisplay" class="pv-Header_ContentWrapper" />
    </div>
    <div ref="pv-body" class="pv-Body">
      <VirtualizationList :data="items" :style="{ minWidth }" data-key="uid" with-virtual-scroll>
        <template #default="{ item }">
          <ProgressModuleGridRow
            :element="item"
            :placement="PROGRESS_MODULE_CELL_PLACEMENTS.PROGRESS_VIEW"
          />
        </template>
      </VirtualizationList>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';
@import '~@/assets/styles/roadmap';
@import '~@/assets/styles/canvas-dimensions';
.pv-Header {
  display: flex;
  position: sticky;
  top: calc(
    var(--objectives-page-header-height, $default-objectives-page-header-height) +
      var(--app-license-banner-height, 0px)
  );
  overflow-x: hidden;
  z-index: 3;
  background: $white;
}

.pv-Header_ContentWrapper {
  min-width: v-bind('minWidth');
  width: 100%;
}

.pv-Body {
  @include styled-native-scrollbar();
  flex: 1 1 0;
  overflow-x: auto;
  position: relative;
  z-index: 2;
  background-color: $white;
  // box-shadow: inset 0 1px 0 $grey-medium;
  overscroll-behavior-x: none;
  height: calc(100% - var(--cdpm-header-height));
}

.pv-Wrapper {
  width: calc(100% - var(--sv-table-width));
  position: relative;

  --cdpm-divider-size: 1px;
  --pm-head-border-bottom-size: 2px;
  --cdpm-divider-color: #{$grey-2-next};
  --cdpm-header-height: 56px;
  --pmcc-cell-padding: 8px 12px;

  .sv-Page-resize-continues & {
    pointer-events: none;
    user-select: none;
  }
}

.pv-Wrapper_Loader {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 4;
}
</style>
